import type * as React from "react";

export type ChipTone =
	| "neutral"
	| "accent"
	| "instagram"
	| "tiktok"
	| "youtube"
	| "substack";

type Palette = { bg: string; fg: string; border: string; countFg: string };

const TONES: Record<ChipTone, Palette> = {
	neutral: {
		bg: "var(--surface-default)",
		fg: "var(--content-primary)",
		border: "var(--border-strong)",
		countFg: "var(--content-faint)",
	},
	accent: {
		bg: "var(--accent-tint)",
		fg: "var(--accent-700)",
		border: "var(--accent-tint)",
		countFg: "var(--accent-500)",
	},
	instagram: {
		bg: "var(--chip-instagram-bg)",
		fg: "var(--chip-instagram-fg)",
		border: "var(--chip-instagram-border)",
		countFg: "var(--content-faint)",
	},
	tiktok: {
		bg: "var(--chip-tiktok-bg)",
		fg: "var(--chip-tiktok-fg)",
		border: "var(--chip-tiktok-border)",
		countFg: "var(--content-faint)",
	},
	youtube: {
		bg: "var(--chip-youtube-bg)",
		fg: "var(--chip-youtube-fg)",
		border: "var(--chip-youtube-border)",
		countFg: "var(--content-faint)",
	},
	substack: {
		bg: "var(--chip-substack-bg)",
		fg: "var(--chip-substack-fg)",
		border: "var(--chip-substack-border)",
		countFg: "var(--content-faint)",
	},
};

const SELECTED: Palette = {
	bg: "var(--accent-500)",
	fg: "var(--content-inverted)",
	border: "var(--accent-500)",
	countFg: "rgb(255 255 255 / 0.7)",
};

type OwnProps = {
	label: React.ReactNode;
	count?: number | string;
	selected?: boolean;
	tone?: ChipTone;
	variant?: "solid" | "dashed";
	size?: "sm" | "md";
	icon?: React.ReactNode;
};

type AsButton = OwnProps & {
	as?: "button";
} & React.ButtonHTMLAttributes<HTMLButtonElement>;
type AsSpan = OwnProps & { as: "span" } & React.HTMLAttributes<HTMLSpanElement>;

export type ChipProps = AsButton | AsSpan;

export function Chip({
	label,
	count,
	selected = false,
	tone = "neutral",
	variant = "solid",
	size = "md",
	icon,
	as = "button",
	style,
	...props
}: ChipProps) {
	const palette = selected ? SELECTED : TONES[tone];
	const pad = size === "sm" ? "8px 13px" : "9px 14px";
	const fontSize = size === "sm" ? 12.5 : 13;

	const chipStyle: React.CSSProperties = {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		padding: pad,
		borderRadius: "var(--radius-pill)",
		fontSize,
		fontWeight: 700,
		lineHeight: 1,
		whiteSpace: "nowrap",
		fontFamily: "var(--font-sans)",
		background: palette.bg,
		color: palette.fg,
		border: `1px ${variant === "dashed" ? "dashed" : "solid"} ${palette.border}`,
		cursor: as === "button" ? "pointer" : "default",
		transition: "background 0.15s, border-color 0.15s, color 0.15s",
		...style,
	};

	const content = (
		<>
			{icon}
			{label}
			{count !== undefined && count !== "" && (
				<span style={{ color: palette.countFg }}>{count}</span>
			)}
		</>
	);

	if (as === "span") {
		return (
			<span
				style={chipStyle}
				{...(props as React.HTMLAttributes<HTMLSpanElement>)}
			>
				{content}
			</span>
		);
	}

	return (
		<button
			type="button"
			aria-pressed={selected}
			style={chipStyle}
			{...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
		>
			{content}
		</button>
	);
}
